import * as React from 'react'

import { Portal } from '../../components/Portal'
import { QuoteNavigation } from './Navigation'
import { QuoteContext } from './QuoteContext'
import { Perf } from './components/ComparePerformance'
import { FundFlowsChart } from './components/FundFlowsChart'
import { InsiderTradingVolumeChart } from './components/InsiderTradingChart'
import { InstitutionalOwnership } from './components/InstitutionalOwnership'
import { ChartContextQuoteHeader, ManualInitQuoteHeader } from './components/Price'
import { QuoteChart } from './components/QuoteChart'
import Statements from './components/Statements/statements'
import { StaticChartHeader } from './components/StaticChartHeader'
import FundamentalCharts from './components/fundamentalCharts'
import EtfHoldings from './etf-holdings'
import { QuoteSubview, QuoteView } from './types'

function tickerElement(ticker: string, selector: string) {
  return document.querySelector(`[data-ticker=${ticker}] ${selector}`)
}

export function QuotePortalComponents({ isStockDetailUpdating }: { isStockDetailUpdating: boolean }) {
  const context = React.useContext(QuoteContext)

  const portalElements = React.useMemo(
    () => ({
      price: {
        static: tickerElement(context.ticker, '.js-quote-price-static'),
        root: tickerElement(context.ticker, '.js-quote-price-root'),
      },
      navigation: {
        static: tickerElement(context.ticker, '.js-quote-navigation-static'),
        root: tickerElement(context.ticker, '.js-quote-navigation-root'),
      },
      static_chart_header: { root: tickerElement(context.ticker, '.quote-menu') },
      fundamental_charts: { root: tickerElement(context.ticker, '.js-quote-fa') },
      statements: { root: tickerElement(context.ticker, '#statements') },
      insider_chart: { root: tickerElement(context.ticker, '.insider-trading-chart') },
      etf_holdings: {
        static: tickerElement(context.ticker, '.js-etf-holdings-static'),
        root: tickerElement(context.ticker, '.js-etf-holdings-root'),
      },
      managers_and_funds: {
        root: tickerElement(context.ticker, '.managers-and-funds'),
      },
    }),
    [context.ticker]
  )

  const isETF = !!portalElements.etf_holdings.root && !!portalElements.etf_holdings.static

  return (
    <>
      {context.index === 0 && !window.isIdeaNotFoundPage && (
        <Portal
          staticElement={portalElements.price.static}
          rootElement={portalElements.price.root}
          toggleStaticElement={false}
        >
          <React.Suspense fallback={null}>
            {context.view === QuoteView.ChartDetail || context.subview === QuoteSubview.ShortInterest ? (
              <ChartContextQuoteHeader />
            ) : (
              <ManualInitQuoteHeader config={window.globalChartConfig} />
            )}
          </React.Suspense>
        </Portal>
      )}

      <Portal
        staticElement={portalElements.navigation.static}
        rootElement={portalElements.navigation.root}
        toggleStaticElement={false}
      >
        <QuoteNavigation />
      </Portal>

      {context.view === QuoteView.ChartDetail && (
        <React.Suspense fallback={null}>
          {context.index === 0 && <QuoteChart />}

          {context.index > 0 && (
            <Portal rootElement={portalElements.static_chart_header.root}>
              <StaticChartHeader isStockDetailUpdating={isStockDetailUpdating} />
            </Portal>
          )}
        </React.Suspense>
      )}

      {context.subview === QuoteSubview.ComparePerf && window.FinvizQuoteTickers && context.index === 0 && <Perf />}

      {isETF && (
        <Portal staticElement={portalElements.etf_holdings.static!} rootElement={portalElements.etf_holdings.root!}>
          <>
            <EtfHoldings />
            <FundFlowsChart className="mt-3" />
          </>
        </Portal>
      )}

      <Portal rootElement={portalElements.fundamental_charts.root}>
        <FundamentalCharts />
      </Portal>

      <Portal rootElement={portalElements.statements.root}>
        <Statements />
      </Portal>

      <Portal rootElement={portalElements.insider_chart.root}>
        <InsiderTradingVolumeChart className="my-6" />
      </Portal>

      <Portal rootElement={portalElements.managers_and_funds.root}>
        <InstitutionalOwnership />
      </Portal>
    </>
  )
}
